/**
 * About Us Page Animations
 * Handles scroll-triggered animations for the About Us page
 */

class NADAboutUsTemplate {
  private element: HTMLElement | null;
  private observer: IntersectionObserver | null = null;
  private animatedElements: Set<Element> = new Set();
  private handleScroll: (() => void) | null = null;
  private updateSectionPositioning: (() => void) | null = null;

  constructor(element: HTMLElement) {
    this.element = element;
    this.init();
  }

  private init(): void {
    this.setupAnimations();
    this.initScrolling();
  }

  private initScrolling(): void {
    // Initialize scrolling logic for sticky sections
    // Only apply on desktop
    if (window.innerWidth <= 768) return;

    const sections = document.querySelectorAll(".nad-about-us > div");
    let currentSection = 0;

    // Listen to window resize to add or remove sticky positioning dynamically
    this.updateSectionPositioning = () => {
      if (window.innerWidth <= 768) {
        sections.forEach((section) => {
          const element = section as HTMLElement;
          element.style.position = "";
          element.style.top = "";
          element.style.zIndex = "";
          element.classList.remove("sticky-active");
        });
      } else {
        sections.forEach((section, index) => {
          const element = section as HTMLElement;
          element.style.position = "sticky";
          element.style.top = "0";
          element.style.zIndex = (1000 + index).toString();
        });
      }
    };

    // Add scroll progress indicator
    const addScrollProgress = () => {
      const progressBar = document.createElement("div");
      progressBar.className = "nad-scroll-progress";
      progressBar.innerHTML = '<div class="nad-scroll-progress__bar"></div>';
      document.body.appendChild(progressBar);
    };

    // Update scroll progress
    const updateScrollProgress = () => {
      const scrollTop = window.pageYOffset;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = (scrollTop / docHeight) * 100;
      
      const progressBar = document.querySelector(".nad-scroll-progress__bar") as HTMLElement;
      if (progressBar) {
        progressBar.style.width = `${scrollPercent}%`;
      }
    };

    // Add section indicators
    const addSectionIndicators = () => {
      const indicators = document.createElement("div");
      indicators.className = "nad-section-indicators";
      
      sections.forEach((_, index) => {
        const dot = document.createElement("div");
        dot.className = `nad-section-indicators__dot ${index === 0 ? 'active' : ''}`;
        dot.addEventListener("click", () => {
          const targetSection = sections[index];
          if (targetSection) {
            targetSection.scrollIntoView({ behavior: "smooth" });
          }
        });
        indicators.appendChild(dot);
      });
      
      document.body.appendChild(indicators);
    };

    // Update active section indicator
    const updateActiveSection = () => {
      const scrollTop = window.pageYOffset;
      const windowHeight = window.innerHeight;
      
      sections.forEach((section, index) => {
        const element = section as HTMLElement;
        const rect = element.getBoundingClientRect();
        const isActive = rect.top <= windowHeight / 2 && rect.bottom >= windowHeight / 2;
        
        if (isActive && index !== currentSection) {
          currentSection = index;
          
          // Update indicators
          const dots = document.querySelectorAll(".nad-section-indicators__dot");
          dots.forEach((dot, dotIndex) => {
            dot.classList.toggle("active", dotIndex === index);
          });
          
          // Add active class for CSS animations
          element.classList.add("sticky-active");
        } else if (!isActive) {
          element.classList.remove("sticky-active");
        }
      });
    };

    // Throttled scroll handler for better performance
    let ticking = false;
    this.handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          updateScrollProgress();
          updateActiveSection();
          ticking = false;
        });
        ticking = true;
      }
    };

    // Initialize all scroll features
    const initializeScrollFeatures = () => {
      if (this.updateSectionPositioning) {
        this.updateSectionPositioning();
      }
      addScrollProgress();
      addSectionIndicators();
      
      // Add scroll listeners
      if (this.handleScroll) {
        window.addEventListener("scroll", this.handleScroll, { passive: true });
      }
    };

    // Attach the resize listener
    if (this.updateSectionPositioning) {
      window.addEventListener("resize", this.updateSectionPositioning);
    }

    // Initial run to set correct positioning on load
    initializeScrollFeatures();
  }

  private setupAnimations(): void {
    // Create intersection observer for scroll-triggered animations
    this.observer = new IntersectionObserver(
      (entries) => this.handleIntersection(entries),
      {
        threshold: 0.1,
        rootMargin: '0px 0px -10% 0px'
      }
    );

    // Observe main sections
    this.observeSections();
    
    // Observe individual cards
    this.observeCards();
    
    // Observe text elements
    this.observeTextElements();
    
    // Observe animated elements with data-animate attributes
    this.observeAnimatedElements();
    
    // Setup enhanced animations
    this.setupFloatingAnimations();
  }

  private observeSections(): void {
    const sections = document.querySelectorAll(`
      .nad-about-us__hero,
      .nad-about-us__mission,
      .nad-about-us__founder,
      .nad-about-us__promise,
      .nad-about-us__products,
      .nad-about-us__values,
      .nad-about-us__objective,
      .nad-about-us__team,
      .nad-about-us__testimonials
    `);
    
    sections.forEach(section => {
      if (this.observer) {
        this.observer.observe(section);
      }
    });
  }

  private observeCards(): void {
    // Observe value cards
    const valueCards = document.querySelectorAll('.nad-about-us__value-card');
    valueCards.forEach(card => {
      if (this.observer) {
        this.observer.observe(card);
      }
    });

    // Observe team cards
    const teamCards = document.querySelectorAll('.nad-about-us__team-card');
    teamCards.forEach(card => {
      if (this.observer) {
        this.observer.observe(card);
      }
    });

    // Observe testimonial cards
    const testimonialCards = document.querySelectorAll('.nad-about-us__testimonial-card, .nad-about-us__promise-testimonial-card');
    testimonialCards.forEach(card => {
      if (this.observer) {
        this.observer.observe(card);
      }
    });

    // Observe product cards
    const productCards = document.querySelectorAll('.nad-about-us__product-card');
    productCards.forEach(card => {
      if (this.observer) {
        this.observer.observe(card);
      }
    });
  }

  private observeTextElements(): void {
    // Observe text elements (titles, subtitles, descriptions)
    const textElements = document.querySelectorAll(`
      .nad-about-us__hero-subtitle,
      .nad-about-us__hero-description,
      .nad-about-us__mission-subtitle,
      .nad-about-us__mission-title,
      .nad-about-us__mission-description,
      .nad-about-us__founder-subtitle,
      .nad-about-us__founder-name,
      .nad-about-us__founder-testimonial,
      .nad-about-us__promise-subtitle,
      .nad-about-us__promise-title,
      .nad-about-us__promise-description,
      .nad-about-us__products-title,
      .nad-about-us__values-title,
      .nad-about-us__values-subtitle,
      .nad-about-us__values-description,
      .nad-about-us__objective-title,
      .nad-about-us__objective-subtitle,
      .nad-about-us__objective-description,
      .nad-about-us__team-title,
      .nad-about-us__team-subtitle,
      .nad-about-us__testimonials-title,
      .nad-about-us__testimonials-subtitle
    `);
    
    textElements.forEach(element => {
      if (this.observer) {
        this.observer.observe(element);
      }
    });
  }

  private observeAnimatedElements(): void {
    // Observe elements with data-animate attributes
    const animatedElements = this.element?.querySelectorAll<HTMLElement>('[data-animate]');
    
    animatedElements?.forEach((el) => {
      if (this.observer) {
        this.observer.observe(el);
      }
    });
  }

  private setupFloatingAnimations(): void {
    // Enhanced floating animations for spheres and ellipses
    this.enhanceSphereAnimations();
    this.enhanceEllipseAnimations();
    this.setupParallaxEffects();
  }

  private enhanceSphereAnimations(): void {
    const spheres = this.element?.querySelectorAll<HTMLElement>('.nad-about-us__mission-sphere');
    
    spheres?.forEach((sphere, index) => {
      // Add random delay to create more natural movement
      const randomDelay = Math.random() * 2;
      sphere.style.animationDelay = `${randomDelay}s`;
      
      // Add hover effect for interactive feel
      sphere.addEventListener('mouseenter', () => {
        sphere.style.animationPlayState = 'paused';
        sphere.style.transform = 'scale(1.1)';
        sphere.style.transition = 'transform 0.3s ease';
      });
      
      sphere.addEventListener('mouseleave', () => {
        sphere.style.animationPlayState = 'running';
        sphere.style.transform = '';
        sphere.style.transition = '';
      });
    });
  }

  private enhanceEllipseAnimations(): void {
    const ellipses = this.element?.querySelectorAll<HTMLElement>('.nad-about-us__mission-ellipse');
    
    ellipses?.forEach((ellipse, index) => {
      // Stagger the animation delays
      ellipse.style.animationDelay = `${index * 0.5}s`;
      
      // Add subtle hover effect
      ellipse.addEventListener('mouseenter', () => {
        ellipse.style.opacity = '0.8';
        ellipse.style.transition = 'opacity 0.3s ease';
      });
      
      ellipse.addEventListener('mouseleave', () => {
        ellipse.style.opacity = '';
        ellipse.style.transition = '';
      });
    });
  }

  private setupParallaxEffects(): void {
    // Add subtle parallax effect to glass elements
    const glassElements = this.element?.querySelectorAll<HTMLElement>('.nad-about-us__hero-glass, .nad-about-us__promise-glass--1, .nad-about-us__promise-glass--2');
    
    if (glassElements && glassElements.length > 0) {
      const handleParallax = () => {
        const scrolled = window.pageYOffset;
        
        glassElements.forEach((element, index) => {
          const speed = 0.5 + (index * 0.1); // Different speeds for different elements
          const yPos = -(scrolled * speed);
          element.style.transform = `translateY(${yPos}px)`;
        });
      };

      window.addEventListener('scroll', handleParallax, { passive: true });
    }
  }

  private handleIntersection(entries: IntersectionObserverEntry[]): void {
    entries.forEach(entry => {
      if (entry.isIntersecting && !this.animatedElements.has(entry.target)) {
        this.animateElement(entry.target);
        this.animatedElements.add(entry.target);
      }
    });
  }

  private animateElement(element: Element): void {
    const htmlElement = element as HTMLElement;
    const animationType = htmlElement.getAttribute('data-animate');
    const delay = htmlElement.getAttribute('data-delay');
    
    if (animationType) {
      // Handle data-animate attributes
      if (delay) {
        setTimeout(() => {
          this.applyAnimation(htmlElement, animationType);
        }, parseInt(delay));
      } else {
        this.applyAnimation(htmlElement, animationType);
      }
    } else {
      // Default animation for elements without data-animate
      element.classList.add('animate-in');
    }
    
    // Log for debugging
    console.log(`About Us animation triggered for:`, element.className);
  }

  private applyAnimation(element: HTMLElement, animationType: string): void {
    // Add the appropriate animation class based on data-animate attribute
    switch (animationType) {
      case 'fade-in':
        // Check if element has specific animation styles (like hero-description)
        if (element.classList.contains('nad-about-us__hero-description') || 
            element.classList.contains('nad-about-us__hero-subtitle')) {
          element.classList.add('is-visible');
        } else {
          element.classList.add('nad-about-us__fade-in', 'is-visible');
        }
        break;
      case 'slide-in-left':
        element.classList.add('nad-about-us__slide-in-left', 'is-visible');
        break;
      case 'slide-in-right':
        element.classList.add('nad-about-us__slide-in-right', 'is-visible');
        break;
      case 'scale-in':
        element.classList.add('nad-about-us__scale-in', 'is-visible');
        break;
    }
  }

  // Public method to manually trigger animations (useful for testing)
  public triggerAnimations(): void {
    const allElements = document.querySelectorAll(`
      .nad-about-us__values,
      .nad-about-us__objective,
      .nad-about-us__team,
      .nad-about-us__testimonials,
      .nad-about-us__value-card,
      .nad-about-us__team-card,
      .nad-about-us__testimonial-card,
      .nad-about-us__values-title,
      .nad-about-us__values-subtitle,
      .nad-about-us__values-description,
      .nad-about-us__objective-title,
      .nad-about-us__objective-subtitle,
      .nad-about-us__objective-description,
      .nad-about-us__team-title,
      .nad-about-us__team-subtitle,
      .nad-about-us__testimonials-title,
      .nad-about-us__testimonials-subtitle
    `);

    allElements.forEach(element => {
      this.animateElement(element);
    });
  }

  // Cleanup method
  public destroy(): void {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    this.animatedElements.clear();
    
    // Clean up scroll-related elements
    const progressBar = document.querySelector(".nad-scroll-progress");
    if (progressBar) {
      progressBar.remove();
    }
    
    const indicators = document.querySelector(".nad-section-indicators");
    if (indicators) {
      indicators.remove();
    }
    
    // Remove scroll event listeners
    if (this.handleScroll) {
      window.removeEventListener("scroll", this.handleScroll);
    }
    if (this.updateSectionPositioning) {
      window.removeEventListener("resize", this.updateSectionPositioning);
    }
  }

  // Static method for automatic initialization following theme pattern
  static initializeAll(): void {
    const elements = document.querySelectorAll<HTMLElement>(".nad-about-us");
    elements.forEach((element) => {
      if (!(element as any)._nadAboutUsInstance) {
        (element as any)._nadAboutUsInstance = new NADAboutUsTemplate(element);
      }
    });
  }

  // Static method for cleanup
  static destroyAll(): void {
    const elements = document.querySelectorAll<HTMLElement>(".nad-about-us");
    elements.forEach((element) => {
      const instance = (element as any)._nadAboutUsInstance;
      if (instance && typeof instance.destroy === "function") {
        instance.destroy();
        delete (element as any)._nadAboutUsInstance;
      }
    });
  }
}

// Export for use in other modules
export { NADAboutUsTemplate };
